import React, { useEffect, useMemo, useState } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { iThinkLogisticsDomesticCarrier } from './Type';
import { setCarrierSettings } from '@Reducers/User';
import {
  getWarehouses,
  OrderTypeOptions,
  ServiceTypeOptions,
  PaymentModeOptions,
  LogisticsOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/iThinkLogisticsDomestic/constants';
import { DropdownOptionType } from '@Components/Dropdown/Type';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}

// ... validation schema from above ...
const iThinkValidationSchema = Yup.object().shape({
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required')
    .default([]),

  pickup_address_id: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Warehouse address value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Warehouse address is required')
    .required('Warehouse address is required')
    .default([]),
  logistics: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Logistics value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Logistics is required')
    .required('Logistics is required')
    .default([]),
  order_type: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Order type value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Order type is required')
    .required('Order type is required')
    .default([]),
  s_type: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Service type value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Service type is required')
    .required('Service type is required')
    .default([]),
  payment_mode: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Payment mode value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Payment mode is required')
    .required('Payment mode is required')
    .default([]),
});

const IThinkLogisticsDomestic = (props: propsTypes) => {
  const { onClose, isEditable } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);
  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);
  const [pickupAddressOptions, setPickupAddressOptions] = useState<DropdownOptionType[]>([]);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  useEffect(() => {
    const accountNumber = accountOptions?.[0]?.value.split('-')[0];
    if (accountNumber) {
      getWarehouses(accountNumber).then(res => {
        setPickupAddressOptions(res);
      });
    }
  }, [accountOptions]);

  const { values, setFieldValue, errors, handleSubmit } = useFormik<iThinkLogisticsDomesticCarrier>({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: iThinkValidationSchema,
    initialValues: {
      ...carrierSetting.ithink_logistics_domestic,
    },
    onSubmit(values) {
      if (userDataById?._id) {
        if (carrierSetting.ithink_logistics_domestic._id) {
          dispatch(updateCarrierSettings({ ...carrierSetting.ithink_logistics_domestic, ...values }));
        } else {
          dispatch(addCarrierSettings({ ...carrierSetting.ithink_logistics_domestic, ...values }));
        }
      } else {
        dispatch(setCarrierSettings({ values, carrierType: 'ithink_logistics_domestic' }));
      }
      onClose();
    },
  });

  return (
    <div>
      <>
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">iThink Logistics Domestic Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label={`iThink Logistics Domestic Account`}
                    required
                    multiple={true}
                    placeholder="Select"
                    name="accountNo"
                    value={values?.accountNo?.map(item => item.value)}
                    options={accountOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={Array.isArray(errors.accountNo) ? errors.accountNo.join(', ') : errors.accountNo}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="pickup_address_id"
                    label={`Warehouse Address`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="apiServiceCode"
                    value={values?.pickup_address_id?.map(item => item.value)}
                    options={pickupAddressOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('pickup_address_id', selectedObj);
                    }}
                    error={errors.pickup_address_id as string}
                  />
                </Grid>
              </Grid>

              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="logistics"
                    label={`Logistics`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="logistics"
                    value={values?.logistics?.map(item => item.value)}
                    options={LogisticsOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('logistics', selectedObj);
                    }}
                    error={errors.logistics as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="order_type"
                    label="Order Type"
                    required
                    multiple={true}
                    placeholder="Select"
                    name="order_type"
                    value={values?.order_type?.map(item => item.value)}
                    options={OrderTypeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('order_type', selectedObj);
                    }}
                    error={errors.order_type as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="payment_mode"
                    label="Payment Mode"
                    required
                    multiple={true}
                    placeholder="Select "
                    name="payment_mode"
                    value={values?.payment_mode?.map(item => item.value)}
                    options={PaymentModeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('payment_mode', selectedObj);
                    }}
                    error={errors.payment_mode as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="s_type"
                    label="Service Type"
                    required
                    multiple={true}
                    placeholder="Select "
                    name="s_type"
                    value={values?.s_type?.map(item => item.value)}
                    options={ServiceTypeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('s_type', selectedObj);
                    }}
                    error={errors.s_type as string}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      type="submit"
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: `4px !important` }}
                      className="w-full"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Save
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        type="button"
                        sx={{ borderRadius: `4px !important` }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    </div>
  );
};

export default IThinkLogisticsDomestic;
